/* global safeArray, num, isoToNb */
// SKATT OG OFFENTLIGE AVGIFTER — kravene, betalingene og seksjonene
//
// Skilt fra backend-tax-recon-view.jsx, som eier kontrollkontoene og
// sammenstillingen. Her ligger det skjermen spør om under hovedavstemmingen:
// hvilke offentlige krav perioden har, hva som er betalt på dem, og de fire
// seksjonene forskuddstrekk, arbeidsgiveravgift, MVA-oppgjør og selskapsskatt.
//
// Ingen av dem regner ut sitt eget fagtall. Forskuddstrekket og
// arbeidsgiveravgiften kommer fra `PlummePayrollObligations` — den samme
// modellen lønnssiden i klientappen bruker til forfall og beløp — MVA-oppgjøret
// fra `buildVatFilingModel`, og bokføringen fra hovedboken. To steder å regne ut
// den samme avgiften ville gitt to svar, og avstemmingen ville avstemt mot seg
// selv.
//
// Det appen IKKE har, later modellen ikke som at den har: Plumme henter ingen
// kravoversikt fra Skatteetaten, og ingen KID. Kravene her er foretakets egne
// beregnede forpliktelser med lovbestemt forfall — ikke et fastsatt krav hentet
// fra Altinn. Seksjonene sier det i klartekst i stedet for å vise en tom kolonne
// som ser ut som en kontroll.

const taxReconRound = (value) => Math.round((Number(value) || 0) * 100) / 100;

const taxReconDateKey = (value) => String(value || '').slice(0, 10);

// Banklinjen som er matchet mot en registrert betaling. Dette er den ENE
// eksterne kilden skatteområdet faktisk har: kravet er foretakets eget tall, men
// at pengene forlot kontoen står i bankutskriften. Koblingen er bankavstemmingens
// egen (`line.matchedEntryId`) — avstemmingen gjetter ikke på beløp og dato.
const taxReconBankTraces = (state) => {
  const traces = new Map();
  for (const statement of safeArray(state.bankStatements)) {
    for (const line of safeArray(statement?.lines)) {
      const entryId = String(line?.matchedEntryId || '');
      if (!entryId || traces.has(entryId)) continue;
      traces.set(entryId, {
        statementId: statement.id || '',
        lineId: line.id || '',
        date: taxReconDateKey(line.date),
        description: line.description || '',
        amount: taxReconRound(line.amount),
      });
    }
  }
  return traces;
};

// Har banken i det hele tatt dekket perioden? Uten en bankutskrift som strekker
// seg inn i måneden finnes ingenting å matche mot, og da er «ikke matchet» ikke
// et funn — det er en manglende kilde. Samme skille som resten av avstemmingen
// bruker mellom «ingen differanse» og «ingen sammenlikning».
const taxReconBankCoverage = (state, range) => safeArray(state.bankStatements).some(
  (statement) => safeArray(statement?.lines).some((line) => {
    const date = taxReconDateKey(line?.date);
    return date >= range.from && date <= range.to;
  }),
);

// Betalingene mot forskuddstrekk og arbeidsgiveravgift. De ligger på foretakets
// eget betalingsregister (`payrollTaxPaymentsByYear`) — samme sted lønnssiden
// registrerer dem, og samme sted hovedboken føres fra.
const taxReconTaxPayments = (state, year, traces) => safeArray(state.payrollTaxPaymentsByYear?.[String(year)])
  .filter((payment) => payment?.status !== 'reversed')
  .map((payment) => ({
    id: String(payment.id || ''),
    date: taxReconDateKey(payment.date),
    kind: payment.kind === 'aga' ? 'aga' : 'withholding',
    runId: payment.runId ? String(payment.runId) : '',
    term: payment.term ? Number(payment.term) : null,
    taxAmount: taxReconRound(payment.taxAmount),
    agaAmount: taxReconRound(payment.agaAmount),
    amount: taxReconRound(num(payment.taxAmount) + num(payment.agaAmount)),
    note: payment.note || '',
    bank: (traces && traces.get(String(payment.id || ''))) || null,
  }))
  .sort((left, right) => left.date.localeCompare(right.date));

// Betalingen er dokumentert når banken bekrefter den. Er ingen av betalingene
// matchet, er kravet «registrert betalt» — ikke «bekreftet betalt», og det er en
// forskjell kontrolløren skal se.
const taxReconBankCover = (payments) => {
  const rows = safeArray(payments);
  const covered = rows.filter((payment) => payment.bank);
  return {
    total: rows.length,
    covered: covered.length,
    complete: rows.length > 0 && covered.length === rows.length,
    lastLineDate: covered.map((payment) => payment.bank.date).sort().pop() || '',
  };
};

// Det lønnskjøringen selv førte til gjeld. Kjøringens egne posteringsrader er
// den ene siden; avviker hovedboken fra dem, er det en føring etterpå — og det
// er nettopp den avstemmingen skal finne.
const taxReconRunPosted = (run, accountCode) => {
  const rows = safeArray(run?.payrollPostingRows).filter(
    (row) => String(row?.accountCode || '') === accountCode,
  );
  if (!rows.length) return null;
  return taxReconRound(rows.reduce((sum, row) => sum + num(row.credit) - num(row.debit), 0));
};

// Statusordene. De står hver for seg fordi ett samlet ord skjuler hva som
// faktisk gjenstår: et krav kan være riktig bokført og likevel ubetalt.
const taxReconPaymentStatus = ({ amount, paid, open, dueDate, periodEnd, lastPaidDate }) => {
  // `id` er den stabile nøkkelen skjermen forgrener på; `label` er kun visning.
  if (amount <= 0.005) return { id: 'notApplicable', label: t('rcs.payStatus.notApplicable'), variant: 'muted' };
  if (open <= 0.005) {
    if (lastPaidDate && dueDate && lastPaidDate > dueDate) return { id: 'paidLate', label: t('rcs.payStatus.paidLate'), variant: 'warning' };
    if (lastPaidDate && lastPaidDate > periodEnd) return { id: 'paidAfterPeriod', label: t('rcs.payStatus.paidAfterPeriod'), variant: 'warning' };
    return { id: 'paid', label: t('rcs.payStatus.paid'), variant: 'success' };
  }
  if (paid > 0.005) return { id: 'partial', label: t('rcs.payStatus.partial'), variant: 'warning' };
  if (dueDate && dueDate <= periodEnd) return { id: 'overdue', label: t('rcs.payStatus.overdue'), variant: 'error' };
  return { id: 'notDue', label: t('rcs.payStatus.notDue'), variant: 'muted' };
};

const taxReconBookingStatus = (reported, booked) => {
  if (booked === null) return { id: 'notBooked', label: t('rcs.bookStatus.notBooked'), variant: 'warning' };
  const difference = taxReconRound(booked - reported);
  if (Math.abs(difference) <= 0.5) return { id: 'booked', label: t('rcs.bookStatus.booked'), variant: 'success' };
  return { id: 'difference', label: t('rcs.bookStatus.difference'), variant: 'warning' };
};


// ------------------------------------------------------------------
// Forskuddstrekk — ett krav per lønnskjøring
// ------------------------------------------------------------------
// Fra 2026 betales forskuddstrekk og utleggstrekk direkte til Skatteetaten
// senest første virkedag etter lønnsutbetalingen. Det finnes ingen løpende
// skattetrekkskonto å avstemme mot, og forfallet er derfor kjøringens eget —
// ikke terminens. Datoen kommer fra `withholdingDueDate`, som også er den
// lønnssiden bruker.
const taxReconWithholdingClaims = ({ state, range, payments, bankCoverage }) => {
  const obligations = window.PlummePayrollObligations
    ? window.PlummePayrollObligations.withholdingObligations(state, range.year)
    : [];
  const runs = new Map(safeArray(state.payrollRuns).map((run) => [String(run.id), run]));

  return obligations
    .map((obligation) => {
      const run = runs.get(String(obligation.runId)) || null;
      const matched = payments.filter(
        (payment) => payment.kind === 'withholding' && payment.runId === String(obligation.runId),
      );
      const paidToDate = taxReconRound(matched
        .filter((payment) => payment.date <= range.to)
        .reduce((sum, payment) => sum + payment.taxAmount, 0));
      const paid = taxReconRound(matched.reduce((sum, payment) => sum + payment.taxAmount, 0));
      const amount = taxReconRound(obligation.amount);
      const booked = run ? taxReconRunPosted(run, '2600') : null;
      const lastPaidDate = matched.map((payment) => payment.date).sort().pop() || '';
      const open = taxReconRound(Math.max(0, amount - paid));
      const bankCover = taxReconBankCover(matched);
      const runLabel = run && run.period
        ? t('rcs.claim.runPeriod', { period: run.period })
        : t('rcs.claim.runDate', { date: isoToNb(obligation.paymentDate) });

      const flags = [];
      if (open > 0.005 && obligation.dueDate && obligation.dueDate <= range.to) flags.push('betaling_mangler');
      if (open > 0.005 && paid > 0.005) flags.push('delvis_betalt');
      if (lastPaidDate && obligation.dueDate && lastPaidDate > obligation.dueDate) flags.push('betalt_for_sent');
      if (lastPaidDate && lastPaidDate > range.to && open <= 0.005) flags.push('betalt_etter_periode');
      if (booked === null) flags.push('ikke_bokfort');
      else if (Math.abs(taxReconRound(booked - amount)) > 0.5) flags.push('bokforingsdifferanse');
      if (run && !run.altinnSent) flags.push('ikke_meldt');
      // Betalingen er registrert, men ingen banklinje bekrefter den. Bare et
      // funn hvis banken faktisk dekker perioden — ellers mangler kilden.
      if (bankCoverage && bankCover.total > 0 && !bankCover.complete) flags.push('uten_bankdekning');

      return {
        id: obligation.id,
        kind: 'forskuddstrekk',
        runId: String(obligation.runId),
        runLabel,
        title: t('rcs.claim.withholdingTitle', { label: runLabel }),
        periodLabel: run && run.period ? run.period : String(obligation.paymentDate).slice(0, 7),
        claimDate: obligation.paymentDate,
        dueDate: obligation.dueDate,
        reported: amount,
        booked,
        paid,
        paidToDate,
        open,
        lastPaidDate,
        payments: matched,
        bankCover,
        reportedLabel: run && run.altinnSent ? t('rcs.reported.withholdingYes') : t('rcs.reported.withholdingNo'),
        claimStatus: { id: 'payrollRun', label: t('rcs.claimStatus.payrollRun'), variant: 'muted' },
        paymentStatus: taxReconPaymentStatus({ amount, paid, open, dueDate: obligation.dueDate, periodEnd: range.to, lastPaidDate }),
        bookingStatus: taxReconBookingStatus(amount, booked),
        flags,
        // Kravet hører til perioden når kjøringen er i den, eller når det
        // fortsatt sto åpent ved periodeslutt. Et krav fra juni som ikke er
        // betalt ligger fortsatt i saldoen på 2600 i august.
        inPeriod: obligation.paymentDate >= range.from && obligation.paymentDate <= range.to,
        openAtPeriodEnd: taxReconRound(Math.max(0, amount - paidToDate)),
      };
    })
    .filter((claim) => claim.claimDate <= range.to
      && (claim.inPeriod || claim.openAtPeriodEnd > 0.005));
};


// ------------------------------------------------------------------
// Arbeidsgiveravgift — ett krav per termin
// ------------------------------------------------------------------
// Avgiften rapporteres og betales terminvis. Beløpet er lønnssystemets eget
// (`agaForTerm`), bokføringen er hovedbokens bevegelse på 2770 fram til
// periodeslutt — det er den sammenlikningen som finner en avgift som er riktig
// beregnet, men feil ført.
const taxReconAgaClaims = ({ state, range, payments, agaBooked, bankCoverage }) => {
  const obligations = window.PlummePayrollObligations
    ? window.PlummePayrollObligations.agaTermObligations(state, range.year)
    : [];

  return obligations
    .map((obligation) => {
      const termStartMonth = (Number(obligation.term) - 1) * 2;
      const matched = payments.filter(
        (payment) => payment.kind === 'aga' && Number(payment.term) === Number(obligation.term),
      );
      const paid = taxReconRound(matched.reduce((sum, payment) => sum + payment.agaAmount, 0));
      const paidToDate = taxReconRound(matched
        .filter((payment) => payment.date <= range.to)
        .reduce((sum, payment) => sum + payment.agaAmount, 0));
      const amount = taxReconRound(obligation.amount);
      const open = taxReconRound(Math.max(0, amount - paid));
      const lastPaidDate = matched.map((payment) => payment.date).sort().pop() || '';
      const booked = agaBooked === null || agaBooked === undefined ? null : taxReconRound(agaBooked);
      const current = range.monthIndex >= termStartMonth && range.monthIndex <= termStartMonth + 1;
      const bankCover = taxReconBankCover(matched);

      const flags = [];
      if (open > 0.005 && obligation.dueDate && obligation.dueDate <= range.to) flags.push('betaling_mangler');
      if (open > 0.005 && paid > 0.005) flags.push('delvis_betalt');
      if (lastPaidDate && obligation.dueDate && lastPaidDate > obligation.dueDate) flags.push('betalt_for_sent');
      // Bokføringen sammenliknes bare for terminen perioden ligger i. En
      // tidligere termin er allerede gjort opp mot sin egen måned, og
      // hovedbokens bevegelse i DENNE måneden sier ingenting om den.
      if (current && booked !== null && Math.abs(taxReconRound(booked - amount)) > 0.5) flags.push('bokforingsdifferanse');
      if (bankCoverage && bankCover.total > 0 && !bankCover.complete) flags.push('uten_bankdekning');

      return {
        id: obligation.id,
        kind: 'aga',
        term: Number(obligation.term),
        title: t('rcs.claim.agaTitle', { term: obligation.term }),
        periodLabel: t('rcs.claim.agaPeriod', { term: obligation.term, year: range.year }),
        claimDate: '',
        dueDate: obligation.dueDate,
        reported: amount,
        booked: current ? booked : null,
        paid,
        paidToDate,
        open,
        lastPaidDate,
        payments: matched,
        bankCover,
        reportedLabel: t('rcs.claimStatus.payrollSystem'),
        claimStatus: { id: 'payrollSystem', label: t('rcs.claimStatus.payrollSystem'), variant: 'muted' },
        paymentStatus: taxReconPaymentStatus({ amount, paid, open, dueDate: obligation.dueDate, periodEnd: range.to, lastPaidDate }),
        bookingStatus: current
          ? taxReconBookingStatus(amount, booked)
          : { id: 'ownTerm', label: t('rcs.bookStatus.ownTerm'), variant: 'muted' },
        flags,
        inPeriod: current,
        openAtPeriodEnd: taxReconRound(Math.max(0, amount - paidToDate)),
      };
    })
    .filter((claim) => claim.inPeriod || claim.openAtPeriodEnd > 0.005);
};


// ------------------------------------------------------------------
// MVA-oppgjøret
// ------------------------------------------------------------------
// Transaksjonskontrollene for MVA gjøres i MVA-avstemmingen. Her står bare
// oppgjøret: hva terminen skylder, om meldingen er levert, og om beløpet er
// betalt. Å gjenta MVA-kontrollene her ville vært et andre sted å avgjøre den
// samme avgiften.
const taxReconVatClaim = ({ term, range, settlementBalance, traces, bankCoverage }) => {
  if (!term) return null;
  const submitted = term.status === 'levert' || term.status === 'levert-betalt';
  const net = taxReconRound(term.netVat);
  const amount = Math.abs(net);
  const paid = taxReconRound(term.betalt);
  const open = taxReconRound(Math.max(0, amount - paid));
  const dueDate = taxReconDateKey(term.dueDate);
  const termDue = range.monthIndex === term.months[1];
  // MVA-oppgjøret registreres på terminen, og legges ut som en bankmatchbar
  // hendelse på samme måte som skattebetalingene. Koblingen er den samme.
  const settlements = safeArray(term.payments).map((record) => ({
    id: String(record?.id || ''),
    date: taxReconDateKey(record?.date),
    amount: taxReconRound(record?.amount),
    note: record?.note || '',
    bank: (traces && traces.get(String(record?.id || ''))) || null,
  }));
  const bankCover = taxReconBankCover(settlements);

  const flags = [];
  if (termDue && !submitted) flags.push('mva_ikke_levert');
  if (submitted && open > 0.005 && dueDate && dueDate <= range.to) flags.push('betaling_mangler');
  if (submitted && open > 0.005 && paid > 0.005) flags.push('delvis_betalt');
  if (bankCoverage && bankCover.total > 0 && !bankCover.complete) flags.push('uten_bankdekning');

  return {
    id: 'mva-' + term.year + '-' + term.n,
    kind: 'mva',
    title: t('rcs.claim.vatTitle', { n: term.n }),
    periodLabel: term.period + ' ' + term.year,
    claimDate: '',
    dueDate,
    // Er meldingen ikke levert, er kravet ikke fastsatt. Utkastet vises for seg
    // — et utkast er ikke et krav.
    reported: submitted ? amount : null,
    draft: amount,
    direction: net < 0 ? 'tilgode' : 'betale',
    booked: settlementBalance === null || settlementBalance === undefined ? null : taxReconRound(settlementBalance),
    paid,
    paidToDate: paid,
    open,
    lastPaidDate: '',
    payments: settlements,
    bankCover,
    reportedLabel: submitted ? t('rcs.reported.vatSubmitted') : t('rcs.reported.vatDraft'),
    claimStatus: submitted
      ? { id: 'fixed', label: t('rcs.claimStatus.fixed'), variant: 'muted' }
      : { id: 'notFixed', label: t('rcs.claimStatus.notFixed'), variant: 'warning' },
    paymentStatus: submitted
      ? taxReconPaymentStatus({ amount, paid, open, dueDate, periodEnd: range.to, lastPaidDate: '' })
      : { id: 'notDue', label: t('rcs.payStatus.notDue'), variant: 'muted' },
    bookingStatus: submitted
      ? taxReconBookingStatus(amount, settlementBalance === null || settlementBalance === undefined ? null : Math.abs(taxReconRound(settlementBalance)))
      : { id: 'onSubmit', label: t('rcs.bookStatus.onSubmit'), variant: 'muted' },
    flags,
    inPeriod: termDue,
    openAtPeriodEnd: open,
    termDue,
    submitted,
  };
};


Object.assign(window, {
  taxReconRound, taxReconDateKey, taxReconTaxPayments, taxReconRunPosted,
  taxReconBankTraces, taxReconBankCoverage, taxReconBankCover,
  taxReconPaymentStatus, taxReconBookingStatus,
  taxReconWithholdingClaims, taxReconAgaClaims, taxReconVatClaim,
});
